import React, { createContext, useCallback, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { FormattedMessage } from "react-intl";
import { useLazyQuery, useMutation } from "@apollo/client";
import * as Sentry from "@sentry/browser";
import { useToast } from "hooks";
import {
  AI_ROLE_BUILDER_TASKS_CONFIG,
  AI_ROLE_BUILDER_TASK_DISPLAY_STATE as DISPLAY_STATE,
  getRoleBuilderTaskDisplayState,
  isTaskPolling,
} from "constants/index";
import { GET_AI_ROLE_BUILDER_TASKS } from "graphql/queries";
import { DISCARD_AI_ROLE_BUILDER_TASK } from "graphql/mutations";

const AIRoleBuilderTasksContext = createContext(null);

/**
 * AIRoleBuilderTasksProvider
 *
 * Server-backed queue of AI Role Builder requests. Unlike the legacy single-job
 * provider there is no localStorage: the queue rehydrates from `aiRoleBuilderTasks`,
 * so it survives a reload and is not scoped to one browser tab.
 *
 * @param {node}    children   Components that consume the queue
 * @param {string}  missionId  Restricts the queue to a single mission (admin mission edit page)
 * @param {boolean} loading    The caller is still resolving missionId; holds off querying
 * @param {object}  config     Polling configuration
 */
const AIRoleBuilderTasksProvider = ({
  children,
  missionId,
  loading = false,
  config = AI_ROLE_BUILDER_TASKS_CONFIG,
}) => {
  const { toast } = useToast();
  const [tasks, setTasks] = useState([]);
  const [initialized, setInitialized] = useState(false);
  const pollingIntervalRef = useRef(null);
  // Ids already announced as ready, so the toast fires once per task rather than once per poll.
  const announcedRef = useRef(new Set());
  // Ids the user has discarded. Polling runs while requests are building, so a poll that
  // was already in flight can still return a row we just removed; without this the row
  // reappears and a second discard fails against an already-discarded task.
  const discardedIdsRef = useRef(new Set());

  const variables = useMemo(() => (missionId ? { missionId } : {}), [missionId]);

  const [getTasks, { loading: fetching }] = useLazyQuery(GET_AI_ROLE_BUILDER_TASKS, { fetchPolicy: "network-only" });
  const [markDiscard, { loading: discarding }] = useMutation(DISCARD_AI_ROLE_BUILDER_TASK);

  const reportError = (error, action) =>
    Sentry.captureException(error, { tags: { feature: "ai_role_builder_queue", action } });

  const fetchTasks = useCallback(async () => {
    // missionId arrives from a query the page is still running. Firing now would send
    // `{}` and get back a wider list than the caller asked for, only to refetch a
    // moment later with the real scope.
    if (loading) return;

    try {
      const { data, error } = await getTasks({ variables });

      if (error) {
        reportError(error, "fetch_tasks");
        return;
      }

      const nextTasks = (data?.aiRoleBuilderTasks || []).filter((task) => !discardedIdsRef.current.has(task.id));

      notifyNewlyFinished(nextTasks, { initialized, toast, announcedRef });
      setTasks(nextTasks);
      setInitialized(true);
    } catch (error) {
      reportError(error, "fetch_tasks");
    }
  }, [getTasks, variables, initialized, toast, loading]);

  const hasPendingTasks = useMemo(() => tasks.some(isTaskPolling), [tasks]);
  // Drives the table skeleton: the caller has not resolved its scope yet, or the first
  // fetch is still out. Poll refetches deliberately do not count.
  const isLoading = loading || (!initialized && fetching);

  /* Initial load, once the caller's scope is settled */
  useEffect(() => {
    if (loading) return;

    fetchTasks();
  }, [variables, loading]);

  /* Poll only while something is still being built */
  useEffect(() => {
    if (loading || !hasPendingTasks) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;

      return undefined;
    }

    if (pollingIntervalRef.current) return undefined;

    pollingIntervalRef.current = setInterval(fetchTasks, config.POLLING_INTERVAL);

    return undefined;
  }, [loading, hasPendingTasks, fetchTasks, config.POLLING_INTERVAL]);

  /* Cleanup on unmount */
  useEffect(
    () => () => {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    },
    []
  );

  /**
   * Adds a freshly submitted task to the queue and resumes polling immediately,
   * so the row appears without waiting for the next tick.
   */
  const submitTask = (mutationResult, resultKey = "submitAiRoleBuilderJob") => {
    const { task } = mutationResult?.[resultKey] || {};

    if (!task) {
      toast.error(<FormattedMessage id="AIRoleBuilder.Error" />);
      fetchTasks();
      return;
    }

    setTasks((previous) => [task, ...previous.filter((item) => item.id !== task.id)]);
  };

  /**
   * Single exit for a queued request. Called with a missionRoleId when the discard
   * follows a successful role creation, and without one for an explicit discard.
   */
  const discardTask = async (id, { missionRoleId } = {}) => {
    // Optimistic: drop the row now so it cannot be actioned twice, and so the queue
    // stops counting it as in-flight. Marking the id keeps an in-flight poll from
    // resurrecting it before the server has caught up.
    discardedIdsRef.current.add(id);
    setTasks((previous) => previous.filter((task) => task.id !== id));

    try {
      await markDiscard({ variables: { input: { id, missionRoleId } } });

      // Only for an explicit discard. When the discard follows a role creation the form
      // has already confirmed that, and a second "discarded" message would read as if
      // the new role had been thrown away.
      if (!missionRoleId) {
        toast.success(<FormattedMessage id="AIRoleBuilder.Tasks.Discard.Success" />);
      }
    } catch (error) {
      discardedIdsRef.current.delete(id);
      reportError(error, "discard_task");
      toast.error(<FormattedMessage id="AIRoleBuilder.Tasks.Discard.Error" />);
      // Re-read rather than restoring a snapshot, which a poll may have superseded.
      fetchTasks();
    }
  };

  const value = useMemo(
    () => ({
      tasks,
      initialized,
      loading: isLoading,
      hasPendingTasks,
      missionId,
      submitTask,
      discardTask,
      discarding,
      refetchTasks: fetchTasks,
      fetching,
    }),
    [tasks, initialized, isLoading, hasPendingTasks, missionId, fetchTasks, discarding, fetching]
  );

  return <AIRoleBuilderTasksContext.Provider value={value}>{children}</AIRoleBuilderTasksContext.Provider>;
};

/**
 * Toasts once for each task that has just left the processing state, as ready or failed.
 * Skipped on the first load, where a finished task only means "finished before you arrived".
 */
const notifyNewlyFinished = (nextTasks, { initialized, toast, announcedRef }) => {
  if (!initialized) {
    nextTasks.filter((task) => !isTaskPolling(task)).forEach((task) => announcedRef.current.add(task.id));

    return;
  }

  nextTasks.forEach((task) => {
    if (isTaskPolling(task) || announcedRef.current.has(task.id)) return;

    announcedRef.current.add(task.id);

    if (getRoleBuilderTaskDisplayState(task) === DISPLAY_STATE.ERROR) {
      toast.error(<FormattedMessage id="AIRoleBuilder.Error" />);

      return;
    }

    toast.success(<FormattedMessage id="AIRoleBuilder.Tasks.Ready.Toast" values={{ label: taskLabel(task) }} />);
  });
};

// The generated job title, available by the time a task reaches the ready state.
const taskLabel = (task) => task?.label || task?.companyName || "";

AIRoleBuilderTasksProvider.propTypes = {
  children: PropTypes.node.isRequired,
  missionId: PropTypes.string,
  loading: PropTypes.bool,
  config: PropTypes.shape({
    POLLING_INTERVAL: PropTypes.number,
  }),
};

export default AIRoleBuilderTasksProvider;

export { AIRoleBuilderTasksContext, taskLabel };
